/**
 * @file: useEntityCardList.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Data layer for the entity-card picker. Reuses the same infinite-list +
 * task-list-item mappers the dashboard uses, so picker cards render
 * identically to TaskCardV2 elsewhere. `order` and `report` both list from
 * task search; `service-record` lists from service-record search.
 */

import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { TaskListItem } from '@euler/model/TaskListItem';
import { ResourceAccessScope } from '@euler/model/enum';
import {
  taskListItemFromServiceRecord,
  taskListItemFromTask,
} from '@euler/model/util';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { isValidLicensePlateNo } from '@euler/utils/validators';
import { useEffect } from 'react';
import { EntityCardKind } from './types';

const LIMIT = 15;

export function useEntityCardList(kind: EntityCardKind, search: string) {
  const { taskService, serviceRecordService } = useServiceFactory();

  // `query` is persisted (stable identity, latest closure) so it always
  // reads the newest `search` even though useInfiniteList only passes the
  // offset.
  const query = usePersistFn(
    async (offset: number): Promise<TaskListItem[]> => {
      const s = search.trim();
      const isPlate = isValidLicensePlateNo(s);
      const licensePlateNo = isPlate ? s.toUpperCase() : undefined;
      const keyword = isPlate ? undefined : s || undefined;
      if (kind === 'service-record') {
        return await serviceRecordService
          .search(
            { scope: ResourceAccessScope.Store, licensePlateNo, keyword },
            null,
            offset,
            LIMIT,
          )
          .then(r => r.map(taskListItemFromServiceRecord));
      }
      return await taskService
        .list({ licensePlateNo, keyword }, offset, LIMIT)
        .then(r => r.map(taskListItemFromTask));
    },
  );

  const { fetch, fetchMore, fetchSinceLastError, ...list } =
    useInfiniteList<TaskListItem>({ limit: LIMIT, query });

  // Reload from the top when the search term (or kind) changes; debounced
  // so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => {
      fetch().catch(onErrorIgnore);
    }, 250);
    return () => clearTimeout(t);
  }, [fetch, search, kind]);

  return { ...list, fetch, fetchMore, fetchSinceLastError };
}
